import { useEffect, useState } from 'react';
import { Inbox, Send, Sparkles, FileText, MessageSquare } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface KnowledgeRequest {
  id: string;
  requester: string;
  avatar: string;
  question: string;
  topic: string;
  received: string;
  status: 'new' | 'drafting' | 'sent';
  urgency: 'normal' | 'high';
  aiDraft?: string;
  attachedFiles?: string[];
}
export function KnowledgeInbox() {
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'inbox' | 'sent' | 'answered'>('inbox');
  const [responseText, setResponseText] = useState('');
  const [requests, setRequests] = useState<KnowledgeRequest[]>([{
    id: '1',
    requester: 'Alex M.',
    avatar: 'AM',
    question: 'How did you handle the Redis cache invalidation in the DNS project?',
    topic: 'Redis Caching',
    received: '2h ago',
    status: 'new',
    urgency: 'normal',
    aiDraft: 'Hi Alex, for the Redis cache invalidation, we implemented a write-through strategy. Whenever a record is updated in the primary DB, we emit an event that the cache worker picks up to invalidate the specific key. This reduced our stale data issues by 99%.',
    attachedFiles: ['cache-strategy.md', 'worker-config.ts']
  }, {
    id: '2',
    requester: 'Team Alpha',
    avatar: 'TA',
    question: 'Do we have any documentation on the new API rate limits?',
    topic: 'API Docs',
    received: '5h ago',
    status: 'drafting',
    urgency: 'high',
    aiDraft: 'The new API rate limits are documented in our internal wiki. The key changes are: 100 requests/minute for authenticated users, 20 requests/minute for unauthenticated. We also added burst allowances.',
    attachedFiles: ['api-limits.md']
  }]);
  const selectedRequestData = requests.find(r => r.id === selectedRequest);
  useEffect(() => {
    if (selectedRequestData?.aiDraft) {
      setResponseText(selectedRequestData.aiDraft);
    }
  }, [selectedRequest, selectedRequestData?.aiDraft]);
  const handleSendResponse = () => {
    if (!selectedRequest || !responseText.trim()) return;
    setRequests(prev => prev.map(req => req.id === selectedRequest ? {
      ...req,
      status: 'sent' as const
    } : req));
    // Show success message
    alert('Response sent successfully!');
    setSelectedRequest(null);
    setResponseText('');
  };
  const handleDecline = () => {
    if (!selectedRequest) return;
    if (confirm('Are you sure you want to decline this request?')) {
      setRequests(prev => prev.filter(req => req.id !== selectedRequest));
      setSelectedRequest(null);
      setResponseText('');
    }
  };
  const handleRegenerateAI = () => {
    if (!selectedRequest) return;
    // Simulate AI regeneration
    const newDraft = responseText + '\n\nAdditionally, we found that implementing a TTL-based expiration strategy helped reduce memory usage by 30%.';
    setResponseText(newDraft);
  };
  const filteredRequests = requests.filter(req => {
    if (activeTab === 'inbox') return req.status === 'new' || req.status === 'drafting';
    if (activeTab === 'sent') return req.status === 'sent';
    if (activeTab === 'answered') return req.status === 'sent';
    return true;
  });
  return <div className="flex h-full bg-surface-900 rounded-xl overflow-hidden border border-surface-600">
      {/* Sidebar List */}
      <div className="w-80 border-r border-surface-600 flex flex-col">
        <div className="p-4 border-b border-surface-600">
          <h2 className="font-semibold text-text-primary mb-4">Knowledge Inbox</h2>
          <div className="flex gap-1 bg-surface-800 p-1 rounded-lg">
            {(['inbox', 'sent', 'answered'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-1.5 text-xs font-medium rounded-md capitalize transition-colors ${activeTab === tab ? 'bg-surface-700 text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'}`}>
                {tab}
              </button>)}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredRequests.map(req => <button key={req.id} onClick={() => setSelectedRequest(req.id)} className={`w-full text-left p-4 border-b border-surface-600 hover:bg-surface-800/50 transition-colors ${selectedRequest === req.id ? 'bg-surface-800/80' : ''}`}>
              <div className="flex justify-between items-start mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-[10px] font-bold">
                    {req.avatar}
                  </div>
                  <span className={`text-sm font-medium ${req.status === 'new' ? 'text-text-primary' : 'text-text-primary'}`}>
                    {req.requester}
                  </span>
                </div>
                <span className="text-[10px] text-text-muted">
                  {req.received}
                </span>
              </div>
              <p className={`text-xs line-clamp-2 mb-2 ${req.status === 'new' ? 'text-text-primary' : 'text-text-secondary'}`}>
                {req.question}
              </p>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-[10px] py-0 h-5">
                  {req.topic}
                </Badge>
                {req.urgency === 'high' && <Badge variant="warning" className="text-[10px] py-0 h-5">
                    Urgent
                  </Badge>}
              </div>
            </button>)}

          {filteredRequests.length === 0 && <div className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-surface-800 flex items-center justify-center mx-auto mb-3">
                <Inbox className="w-6 h-6 text-text-muted" />
              </div>
              <p className="text-sm text-text-secondary">No requests yet</p>
            </div>}
        </div>
      </div>

      {/* Detail View */}
      <div className="flex-1 flex flex-col bg-surface-900/50">
        {selectedRequestData ? <>
            <div className="p-6 border-b border-surface-600">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center font-bold">
                  {selectedRequestData.avatar}
                </div>
                <div>
                  <div className="font-medium text-text-primary text-lg">
                    {selectedRequestData.requester}
                  </div>
                  <div className="text-xs text-text-secondary">
                    Product Team • Asking about {selectedRequestData.topic}
                  </div>
                </div>
              </div>
              <div className="bg-surface-800/50 p-4 rounded-xl border border-surface-500 text-text-primary leading-relaxed">
                {selectedRequestData.question}
              </div>
            </div>

            <div className="flex-1 p-6 overflow-y-auto">
              <div className="mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-teal-400" />
                <span className="text-xs font-bold text-teal-400 uppercase tracking-wider">
                  AI Draft
                </span>
                <button onClick={handleRegenerateAI} className="ml-auto text-xs text-teal-400 hover:text-teal-300 transition-colors">
                  Regenerate
                </button>
              </div>

              {selectedRequestData.attachedFiles && selectedRequestData.attachedFiles.length > 0 && <div className="mb-4 flex items-center gap-2">
                    {selectedRequestData.attachedFiles.map((file, i) => <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded bg-surface-900/50 border border-surface-500/50 text-xs text-text-secondary">
                        <FileText className="w-3 h-3" />
                        <span>{file}</span>
                      </div>)}
                  </div>}

              <textarea value={responseText} onChange={e => setResponseText(e.target.value)} className="w-full bg-surface-800 border border-surface-500 rounded-xl p-4 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-teal-500 min-h-[200px] resize-y" placeholder="Edit your response..." />
            </div>

            <div className="p-6 border-t border-surface-600 flex items-center justify-between bg-surface-900">
              <Button variant="ghost" size="sm" onClick={handleDecline}>
                Decline
              </Button>
              <div className="flex gap-3">
                <Button variant="secondary" size="sm" onClick={handleRegenerateAI}>
                  Regenerate Draft
                </Button>
                <Button className="bg-teal-600 hover:bg-teal-700 text-text-primary border-none" size="sm" onClick={handleSendResponse} disabled={!responseText.trim()}>
                  <Send className="w-4 h-4 mr-2" />
                  Send Response
                </Button>
              </div>
            </div>
          </> : <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 rounded-2xl bg-surface-800 flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-text-muted" />
            </div>
            <h3 className="text-lg font-medium text-text-primary mb-2">
              Select a request
            </h3>
            <p className="text-sm text-text-muted max-w-xs">
              View and respond to knowledge requests from across the
              organization.
            </p>
          </div>}
      </div>
    </div>;
}